import React, { useEffect, useMemo, useState } from "react";

// Chip option sets
const NAME_OPTIONS = [
    { value: "Accutane", label: "Accutane" },
    { value: "Azelaic Acid", label: "Azelaic Acid" },
    { value: "Benzoyl Peroxide", label: "Benzoyl Peroxide" },
    { value: "Clindamycin", label: "Clindamycin" },
    { value: "Doxycycline", label: "Doxycycline" },
    { value: "Spironolactone", label: "Spironolactone" },
    { value: "Tretinoin/Tazarotene", label: "Tretinoin/Tazarotene" },
    { value: "Other", label: "Other" },
];

const DURATION_OPTIONS = [
    { value: "Currently on it", label: "Currently on it" },
    { value: "Previously - days/weeks", label: "Previously - days/weeks" },
    { value: "Previously - months or more", label: "Previously - months or more" },
];

const HELP_OPTIONS = [
    { value: "Helped a lot", label: "Helped a lot" },
    { value: "Helped a little", label: "Helped a little" },
    { value: "Didn't help", label: "Didn't help" },
];

const DOSAGE_SPIRONOLACTONE = [
    { value: "25mg", label: "25mg" },
    { value: "50mg", label: "50mg" },
    { value: "100mg", label: "100mg" },
    { value: "Not sure", label: "Not sure" },
];

const DOSAGE_TRETINOIN_TAZ = [
    { value: "0.025%", label: "0.025%" },
    { value: "0.05%", label: "0.05%" },
    { value: "0.1%", label: "0.1%" },
    { value: "Not sure", label: "Not sure" },
];

function needsDosage(name) {
    return name === "Spironolactone" || name === "Tretinoin/Tazarotene";
}
function displayName(med) {
    return med.name === "Other" ? (med.customName || "Other") : med.name;
}
function isComplete(med, isAcneTreatment) {
    if (!med?.name || !med?.duration || !med?.help) return false;
    if (isAcneTreatment && med.name === "Other" && !med.customName) return false;
    if (isAcneTreatment && needsDosage(med.name) && !med.dosage) return false;
    return true;
}

const PrescriptionsQuestion = ({ onSubmit, onNext, initialValue, ensureFullyVisible, treatmentAnswer }) => {
    // Check if treatment is Acne - if so, use chip-based UI, otherwise use text-input UI
    const isAcneTreatment = treatmentAnswer === "Acne";

    // For non-Acne: progressive form mode (text input → duration → help)
    const [completedNonAcne, setCompletedNonAcne] = useState([]);
    const [currentNonAcne, setCurrentNonAcne] = useState({
        name: "",
        duration: "",
        help: "",
    });
    const nonAcneNameInputRef = React.useRef(null);

    // For Acne: chip-based mode (existing behavior)
    const [completed, setCompleted] = useState([]);
    const [current, setCurrent] = useState({
        name: "",
        customName: "",
        duration: "",
        help: "",
        dosage: "",
    });
    const [error, setError] = useState("");
    const customNameInputRef = React.useRef(null);

    // Prefill completed entries from initialValue (Acne mode)
    useEffect(() => {
        if (isAcneTreatment && Array.isArray(initialValue) && initialValue.length > 0) {
            setCompleted(initialValue.map((m, i) => ({ ...m, _id: m._id || `m-${i}-${Date.now()}` })));
        }
    }, [initialValue, isAcneTreatment]);

    // Prefill from initialValue (non-Acne mode)
    useEffect(() => {
        if (!isAcneTreatment && Array.isArray(initialValue) && initialValue.length > 0) {
            // For non-Acne, initialValue might be array of strings or array of objects
            const meds = initialValue.map(m => {
                if (typeof m === "string") {
                    // Legacy format: just string, create object with empty duration/help
                    return { name: m, duration: "", help: "", _id: `m-${Date.now()}-${Math.random()}` };
                }
                // Object format: ensure _id exists
                return { ...m, _id: m._id || `m-${Date.now()}-${Math.random()}` };
            });
            setCompletedNonAcne(meds.filter(m => m.name));
        }
    }, [initialValue, isAcneTreatment]);

    // Delayed autofocus for text input (non-Acne mode)
    useEffect(() => {
        if (!isAcneTreatment) {
            const timer = setTimeout(() => {
                nonAcneNameInputRef.current?.focus();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [isAcneTreatment]);

    // Delayed autofocus when "Other" is selected (Acne mode)
    useEffect(() => {
        if (isAcneTreatment && current.name === "Other") {
            const timer = setTimeout(() => {
                customNameInputRef.current?.focus();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [current.name, isAcneTreatment]);

    // Don't auto-scroll when form fields appear - let user control their position
    // The Continue button has onFocus handler to scroll into view when needed

    // Prevent selecting the same med twice (by display name) - Acne mode only
    const takenNames = useMemo(
        () => isAcneTreatment ? new Set(completed.map(displayName).filter(Boolean)) : new Set(),
        [completed, isAcneTreatment]
    );

    // Chip toggles
    const pickName = (val, event) => {
        if (val !== "Other" && takenNames.has(val)) return;
        // Blur the button after click to prevent focus from persisting and looking like selection
        if (event && event.currentTarget) {
            event.currentTarget.blur();
        }

        setError("");
        setCurrent((c) => ({
            name: val,
            customName: val === "Other" ? c.customName : "",
            duration: "",
            help: "",
            dosage: "",
        }));
    };
    const pickDuration = (val, event) => {
        if (event && event.currentTarget) {
            event.currentTarget.blur();
        }
        setCurrent((c) => ({ ...c, duration: val }));
    };
    const pickHelp = (val, event) => {
        if (event && event.currentTarget) {
            event.currentTarget.blur();
        }
        setCurrent((c) => ({ ...c, help: val }));
    };
    const pickDosage = (val, event) => {
        if (event && event.currentTarget) {
            event.currentTarget.blur();
        }
        setCurrent((c) => ({ ...c, dosage: val }));
    };

    const handleAdd = () => {
        if (!isComplete(current, true)) {
            setError("Please complete all required fields.");
            return;
        }
        const entry = {
            name: current.name,
            customName: current.customName?.trim() || "",
            duration: current.duration,
            help: current.help,
            dosage: needsDosage(current.name) ? current.dosage : "",
            _id: `m-${Date.now()}`,
        };
        setCompleted((list) => [entry, ...list]);
        // reset progressive row
        setCurrent({ name: "", customName: "", duration: "", help: "", dosage: "" });
        setError("");

        // Don't aggressively scroll after adding - let user continue working
        // The Continue button will scroll into view when focused
    };

    const removeCompleted = (id) => {
        setCompleted((list) => list.filter((m) => m._id !== id));
        // Don't scroll on removal - maintain scroll position
    };

    // Non-Acne mode handlers
    const pickDurationNonAcne = (val, event) => {
        if (event && event.currentTarget) {
            event.currentTarget.blur();
        }
        setCurrentNonAcne((c) => ({ ...c, duration: val }));
    };
    const pickHelpNonAcne = (val, event) => {
        if (event && event.currentTarget) {
            event.currentTarget.blur();
        }
        setCurrentNonAcne((c) => ({ ...c, help: val }));
    };

    const handleAddNonAcne = () => {
        if (!isComplete(currentNonAcne, false)) {
            setError("Please complete all required fields.");
            return;
        }
        const entry = {
            name: currentNonAcne.name.trim(),
            duration: currentNonAcne.duration,
            help: currentNonAcne.help,
            _id: `m-${Date.now()}`,
        };
        setCompletedNonAcne((list) => [entry, ...list]);
        // reset progressive row
        setCurrentNonAcne({ name: "", duration: "", help: "" });
        setError("");

        // Don't aggressively scroll after adding - let user continue working
        // The Continue button will scroll into view when focused
    };

    const removeCompletedNonAcne = (id) => {
        setCompletedNonAcne((list) => list.filter((m) => m._id !== id));
        // Don't scroll on removal - maintain scroll position
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (isAcneTreatment) {
            // Acne mode: existing logic
            if (completed.length === 0) {
                setError("Please add at least one medication.");
                return;
            }
            if (isComplete(current, true)) {
                setError(`Click "Add ${displayName(current)}" or clear the current entry before continuing.`);
                return;
            }
            onSubmit(completed);
        } else {
            // Non-Acne mode: progressive form
            if (completedNonAcne.length === 0) {
                setError("Please add at least one medication.");
                return;
            }
            if (isComplete(currentNonAcne, false)) {
                setError(`Click "Add ${currentNonAcne.name}" or clear the current entry before continuing.`);
                return;
            }
            // Submit as array of objects with name, duration, help
            onSubmit(completedNonAcne);
        }
        onNext();
    };

    const canContinue = isAcneTreatment 
        ? (completed.length > 0 && !isComplete(current, true))
        : (completedNonAcne.length > 0 && !isComplete(currentNonAcne, false));

    // Render non-Acne mode (text input → duration → help)
    if (!isAcneTreatment) {
        return (
            <form onSubmit={handleSubmit} className="input-form input-form--stacked">
                <div className="input-field-container" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {/* Medication Name */}
                    <div style={{ fontWeight: 700 }}>Medication Name *</div>
                    <input
                        type="text"
                        className="input-field"
                        placeholder="Enter medication name"
                        value={currentNonAcne.name}
                        onChange={(e) => setCurrentNonAcne((c) => ({ ...c, name: e.target.value }))}
                        ref={nonAcneNameInputRef}
                    />

                    {/* Duration chips */}
                    {currentNonAcne.name.trim() && (
                        <>
                            <div style={{ fontWeight: 700, marginTop: 8 }}>Duration *</div>
                            <div
                                className="photo-summary-grid"
                                style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}
                            >
                                {DURATION_OPTIONS.map((opt) => {
                                    const selected = currentNonAcne.duration === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={`treatment-button ${selected ? "treatment-button-selected" : ""}`}
                                            onClick={(e) => pickDurationNonAcne(opt.value, e)}
                                            aria-pressed={selected}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* Help chips */}
                    {currentNonAcne.duration && (
                        <>
                            <div style={{ fontWeight: 700, marginTop: 8 }}>Did it help? *</div>
                            <div
                                className="photo-summary-grid"
                                style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}
                            >
                                {HELP_OPTIONS.map((opt) => {
                                    const selected = currentNonAcne.help === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={`treatment-button ${selected ? "treatment-button-selected" : ""}`}
                                            onClick={(e) => pickHelpNonAcne(opt.value, e)}
                                            aria-pressed={selected}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* Add current */}
                    {currentNonAcne.help && (
                        <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
                            <button
                                type="button"
                                className="multiselect-submit-button"
                                onClick={handleAddNonAcne}
                                disabled={!isComplete(currentNonAcne, false)}
                            >
                                {`Add ${currentNonAcne.name.trim() || "medication"}`}
                            </button>
                        </div>
                    )}

                    {/* Completed cards */}
                    {completedNonAcne.length > 0 && (
                        <div style={{ marginTop: 8, width: "100%" }}>
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>
                                Your Medications ({completedNonAcne.length})
                            </div>
                            <div
                                style={{
                                    display: "grid",
                                    gap: 8,
                                    gridTemplateColumns: "1fr",
                                }}
                            >
                                {completedNonAcne.map((m) => (
                                    <div
                                        key={m._id}
                                        className="message-bubble user-message-bubble"
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        <div>
                                            <div><strong>{m.name}</strong></div>
                                            <div>Duration: {m.duration}</div>
                                            <div>Helpfulness: {m.help}</div>
                                        </div>
                                        <button
                                            type="button"
                                            className="photo-remove-button"
                                            onClick={() => removeCompletedNonAcne(m._id)}
                                            aria-label={`Remove ${m.name}`}
                                            title="Remove"
                                            style={{ position: "static" }}
                                        >
                                            x
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {error && <div className="error-message">{error}</div>}

                    {/* Final continue */}
                    <button
                        type="submit"
                        className="multiselect-submit-button"
                        disabled={!canContinue}
                        style={{ alignSelf: "center" }}
                        onFocus={() => ensureFullyVisible?.()}
                    >
                        Continue
                    </button>
                </div>
            </form>
        );
    }

    // Render Acne mode (chip-based UI)
    return (
        <form onSubmit={handleSubmit} className="input-form input-form--stacked">
            <div className="input-field-container" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Medication Name */}
                <div style={{ fontWeight: 700 }}>Medication Name *</div>
                <div
                    className="photo-summary-grid"
                    style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}
                >
                    {NAME_OPTIONS.map((opt) => {
                        const selected = current.name === opt.value;
                        const disabled = opt.value !== "Other" && takenNames.has(opt.value) && !selected;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                className={`treatment-button ${selected ? "treatment-button-selected" : ""}`}
                                onClick={(e) => (disabled ? null : pickName(opt.value, e))}
                                disabled={disabled}
                                aria-pressed={selected}
                                title={disabled ? "Already added" : opt.label}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>

                {/* 1a) Custom name if Other */}
                {current.name === "Other" && (
                    <input
                        type="text"
                        className="input-field"
                        placeholder="Enter medication name"
                        value={current.customName}
                        onChange={(e) => setCurrent((c) => ({ ...c, customName: e.target.value }))}
                        ref={customNameInputRef}
                    />
                )}

                {/* 2) Duration chips */}
                {current.name && (current.name !== "Other" ? true : current.customName.trim()) && (
                    <>
                        <div style={{ fontWeight: 700, marginTop: 8 }}>Duration *</div>
                        <div
                            className="photo-summary-grid"
                            style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}
                        >
                            {DURATION_OPTIONS.map((opt) => {
                                const selected = current.duration === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`treatment-button ${selected ? "treatment-button-selected" : ""}`}
                                        onClick={(e) => pickDuration(opt.value, e)}
                                        aria-pressed={selected}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* 3) Help chips */}
                {current.duration && (
                    <>
                        <div style={{ fontWeight: 700, marginTop: 8 }}>Did it help? *</div>
                        <div
                            className="photo-summary-grid"
                            style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}
                        >
                            {HELP_OPTIONS.map((opt) => {
                                const selected = current.help === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`treatment-button ${selected ? "treatment-button-selected" : ""}`}
                                        onClick={(e) => pickHelp(opt.value, e)}
                                        aria-pressed={selected}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* 4) Dosage chips (conditional) */}
                {current.help && needsDosage(current.name) && (
                    <>
                        <div style={{ fontWeight: 700, marginTop: 8 }}>Dosage *</div>
                        <div
                            className="photo-summary-grid"
                            style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}
                        >
                            {(current.name === "Spironolactone" ? DOSAGE_SPIRONOLACTONE : DOSAGE_TRETINOIN_TAZ).map((opt) => {
                                const selected = current.dosage === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`treatment-button ${selected ? "treatment-button-selected" : ""}`}
                                        onClick={(e) => pickDosage(opt.value, e)}
                                        aria-pressed={selected}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Add current */}
                {(current.help && !needsDosage(current.name)) ||
                    (current.dosage && needsDosage(current.name)) ? (
                    <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
                        <button
                            type="button"
                            className="multiselect-submit-button"
                            onClick={handleAdd}
                            disabled={!isComplete(current, true)}
                        >
                            {`Add ${displayName(current) || "medication"}`}
                        </button>
                    </div>
                ) : null}

                {/* Completed cards */}
                {completed.length > 0 && (
                    <div style={{ marginTop: 8, width: "100%" }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>
                            Your Medications ({completed.length})
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gap: 8,
                                gridTemplateColumns: "1fr",   // full-width single column
                            }}
                        >
                            {completed.map((m) => (
                                <div
                                    key={m._id}
                                    className="message-bubble user-message-bubble"
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        width: "100%",              // stretch to container width
                                    }}
                                >
                                    <div>
                                        <div><strong>{displayName(m)}</strong></div>
                                        <div>Duration: {m.duration}</div>
                                        {m.dosage ? <div>Dosage: {m.dosage}</div> : null}
                                        <div>Helpfulness: {m.help}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className="photo-remove-button"
                                        onClick={() => removeCompleted(m._id)}
                                        aria-label={`Remove ${displayName(m)}`}
                                        title="Remove"
                                        style={{ position: "static" }}
                                    >
                                        x
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}

                {/* Final continue */}
                <button
                    type="submit"
                    className="multiselect-submit-button"
                    disabled={!canContinue}
                    style={{ alignSelf: "center" }}
                    onFocus={() => ensureFullyVisible?.()}
                >
                    Continue
                </button>
            </div>
        </form>
    );
};

export default PrescriptionsQuestion;
